import { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Filter, MapPin, Navigation, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../stores/auth';
import { citiesApi } from '../../utils/api';
import { City } from '../../types';
import { ADMIN_LECCE_COORDS } from '../../config/filters';

interface CityGeographicFilterProps {
  selectedCity: City | null;
  setSelectedCity: (city: City | null) => void;
  selectedProvince: string;
  setSelectedProvince: (province: string) => void;
  selectedRegion: string;
  setSelectedRegion: (region: string) => void;
  useLocation: boolean;
  setUseLocation: (use: boolean) => void;
  useFixedLocation: boolean;
  setUseFixedLocation: (use: boolean) => void;
  location: { lat: number; lng: number } | null;
  setLocation: (location: { lat: number; lng: number } | null) => void;
  locationStatus: string;
  setLocationStatus: (status: string) => void;
  onRadiusChange?: (radius: number) => void;
  currentRadius?: number;
}

// Range di distanza
const distanceRanges = [
  { label: 'Tutte', max: -1 },
  { label: '20 km', max: 20 },
  { label: '50 km', max: 50 },
  { label: '100 km', max: 100 },
  { label: '150 km', max: 150 },
  { label: '200 km', max: 200 },
  { label: '500 km', max: 500 },
];

export default function CityGeographicFilter({
  selectedCity,
  setSelectedCity,
  selectedProvince,
  setSelectedProvince,
  selectedRegion,
  setSelectedRegion,
  useLocation,
  setUseLocation,
  useFixedLocation,
  setUseFixedLocation,
  location,
  setLocation,
  locationStatus,
  setLocationStatus,
  onRadiusChange,
  currentRadius = 100,
}: CityGeographicFilterProps) {
  const { user } = useAuth();
  const dealerCity = String(user?.citta || user?.city || '');
  
  const [expanded, setExpanded] = useState(true);
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [useLecceAsReference, setUseLecceAsReference] = useState(false);

  // Query per cercare città
   const { data: cities = [] } = useQuery(
     ['citiesSearch', citySearch],
     () => citiesApi.getCities(citySearch).then((res: any) => {
       const responseData = res.data;
       if (Array.isArray(responseData)) return responseData;
       if (responseData?.data && Array.isArray(responseData.data)) return responseData.data;
       return [];
     }),
     { enabled: citySearch.length >= 2 }
   );

  // Query per province
   const { data: provinces = [] } = useQuery(
     'provinces',
     () => citiesApi.getProvinces().then((res: any) => {
       const responseData = res.data;
       if (Array.isArray(responseData)) return responseData;
       if (responseData?.data && Array.isArray(responseData.data)) return responseData.data;
       return [];
     }),
     {}
   );

  // Query per regioni
   const { data: regions = [] } = useQuery(
     'regions',
     () => citiesApi.getRegions().then((res: any) => {
       const responseData = res.data;
       if (Array.isArray(responseData)) return responseData;
       if (responseData?.data && Array.isArray(responseData.data)) return responseData.data;
       return [];
     }),
     {}
   );

  // Query per città per provincia
   const { data: citiesByProvince = [] } = useQuery(
     ['citiesByProvince', selectedProvince],
     () => citiesApi.getCitiesByProvince(selectedProvince).then((res: any) => res.data),
     { enabled: !!selectedProvince }
   );

  // Query per città per regione
   const { data: citiesByRegion = [] } = useQuery(
     ['citiesByRegion', selectedRegion],
     () => citiesApi.getCitiesByRegion(selectedRegion).then((res: any) => res.data),
     { enabled: !!selectedRegion }
   );

  // Determina quali città mostrare
  const displayCities = useMemo(() => {
    if (selectedProvince) return citiesByProvince || [];
    if (selectedRegion) return citiesByRegion || [];
    return cities || [];
  }, [selectedProvince, selectedRegion, cities, citiesByProvince, citiesByRegion]);

  // Gestione ricerca città
  const handleCitySearchChange = (value: string) => {
    setCitySearch(value);
    setSelectedProvince('');
    setSelectedRegion('');
    if (value.length >= 2) {
      setShowCityDropdown(true);
    } else {
      setShowCityDropdown(false);
    }
  };

  // Selezione città
  const handleCitySelect = (city: any) => {
    const cityLocation = { lat: parseFloat(city.lat), lng: parseFloat(city.lng) };
    setSelectedCity(city);
    setCitySearch(city.name);
    setShowCityDropdown(false);
    setUseLocation(false);
    setUseFixedLocation(false);
    setUseLecceAsReference(false);
    setLocation(cityLocation);
    setLocationStatus(`Città: ${city.name}, ${city.province}`);
  };

  // Toggle Usa posizione attuale (GPS)
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('Geolocalizzazione non supportata');
      return;
    }

    setLocationStatus('Localizzazione in corso...');
     navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
          setLocation(newLocation);
          setUseLocation(true);
          setUseFixedLocation(false);
          setUseLecceAsReference(false);
          setSelectedCity(null);
          setCitySearch('');
          setLocationStatus('Posizione attuale rilevata!');
          
          setTimeout(() => setLocationStatus(''), 2000);
        },
      (error) => {
        let message = 'Errore nella localizzazione';
        if (error.code === error.PERMISSION_DENIED) message = 'Permesso negato';
        else if (error.code === error.POSITION_UNAVAILABLE) message = 'Posizione non disponibile';
        else if (error.code === error.TIMEOUT) message = 'Timeout';
        setLocationStatus(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // Toggle Usa Lecce come riferimento
  const handleUseLecce = () => {
    setUseLecceAsReference(true);
    setUseFixedLocation(true);
    setUseLocation(false);
    setSelectedCity(null);
    setCitySearch('');
    setLocation(ADMIN_LECCE_COORDS);
    setLocationStatus(`Posizione: ${dealerCity || 'Lecce'} (riferimento)`);

    setTimeout(() => setLocationStatus(''), 2000);
  };

  // Gestione cambio provincia
  const handleProvinceChange = (province: string) => {
    setSelectedProvince(province);
    setSelectedRegion('');
    setCitySearch('');
    setSelectedCity(null);
    setUseLecceAsReference(false);
    
    if (province) {
      setUseFixedLocation(false);
      setUseLocation(false);
      setLocationStatus(`Provincia: ${province}`);
    } else {
      setLocationStatus('');
    }
  };

  // Gestione cambio regione
  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    setSelectedProvince('');
    setCitySearch('');
    setSelectedCity(null);
    setUseLecceAsReference(false);
    
    if (region) {
      setUseFixedLocation(false);
      setUseLocation(false);
      setLocationStatus(`Regione: ${region}`);
    } else {
      setLocationStatus('');
    }
  };

  // Reset filtri posizione
  const handleReset = () => {
    setSelectedCity(null);
    setSelectedProvince('');
    setSelectedRegion('');
    setCitySearch('');
    setUseLocation(false);
    setUseFixedLocation(false);
    setUseLecceAsReference(false);
    setLocation(null);
    setLocationStatus('');
    setShowCityDropdown(false);
  };

  // Calcola distanza visualizzata
  const getDistanceDisplay = () => {
    if (selectedCity) {
      return `${selectedCity.name} (${selectedCity.province})`;
    }
    if (useLecceAsReference || useFixedLocation) {
      return dealerCity || 'Lecce';
    }
    if (useLocation && location) {
      return 'Posizione attuale';
    }
    return 'Nessuna posizione';
  };

  const hasActiveFilters = useLocation || useFixedLocation || selectedCity || selectedProvince || selectedRegion;

  return (
    <div className="rounded-lg" style={{ background: 'var(--glass-card-bg, #fff)' }}>
      {/* Header espandibile */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 text-left transition-colors"
        style={{
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--glass-hover-bg, #f9fafb)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <h3 className="font-medium flex items-center gap-2" style={{ color: 'var(--glass-text-primary, #242424)' }}>
          <MapPin className="w-4 h-4" style={{ color: 'var(--glass-primary, #0099ff)' }} />
          Filtro Geografico
        </h3>
        {expanded ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} /> : <ChevronDown className="w-4 h-4" style={{ color: 'var(--glass-text-secondary, #898989)' }} />}
      </button>

      {expanded && (
        <div className="p-3 space-y-4" style={{ borderTop: '1px solid var(--glass-card-border, #e5e7eb)' }}>
          {/* Status posizione attiva */}
          {locationStatus && (
            <div 
              className="text-xs px-2 py-1 rounded"
              style={{
                backgroundColor: locationStatus.includes('Errore') || locationStatus.includes('negato') 
                  ? 'var(--glass-danger-light, rgba(239,68,68,0.1))' 
                  : 'var(--glass-primary-light)',
                color: locationStatus.includes('Errore') || locationStatus.includes('negato') 
                  ? 'var(--glass-danger, #dc2626)' 
                  : 'var(--glass-primary, #0099ff)'
              }}
            >
              {locationStatus}
            </div>
          )}

          {/* Toggle: Usa Lecce come riferimento */}
          <button
            onClick={handleUseLecce}
            className="w-full py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all"
            style={{
              backgroundColor: useLecceAsReference ? 'var(--glass-success-light)' : 'var(--glass-card-bg, #fff)',
              color: useLecceAsReference ? 'var(--glass-success, #22c55e)' : 'var(--glass-text-primary, #242424)',
              border: '1px solid ' + (useLecceAsReference ? 'var(--glass-success, rgba(34, 197, 94, 0.3))' : 'var(--glass-card-border, rgba(34, 42, 53, 0.08))')
            }}
          >
            <MapPin className="w-4 h-4" />
            Usa {dealerCity || 'Lecce'} come riferimento
          </button>

          {/* Toggle: Usa posizione attuale (GPS) */}
          <button
            onClick={handleUseMyLocation}
            className="w-full py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all"
            style={{
              backgroundColor: useLocation ? 'var(--glass-primary-light)' : 'var(--glass-card-bg, #fff)',
              color: useLocation ? 'var(--glass-primary, #0099ff)' : 'var(--glass-text-primary, #242424)',
              border: '1px solid ' + (useLocation ? 'var(--glass-primary, rgba(0, 153, 255, 0.3))' : 'var(--glass-card-border, rgba(34, 42, 53, 0.08))')
            }}
          >
            <Navigation className="w-4 h-4" />
            Usa posizione attuale (GPS)
          </button>

          {/* Ricerca città con autocomplete */}
          <div className="relative">
            <label className="block text-xs mb-1" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Cerca città</label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--glass-text-disabled)' }} />
              <input
                type="text"
                value={citySearch}
                onChange={(e) => handleCitySearchChange(e.target.value)}
                onFocus={() => citySearch.length >= 2 && displayCities.length > 0 && setShowCityDropdown(true)}
                placeholder="Digita il nome di una città..."
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--glass-card-bg, #fff)',
                  border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                  color: 'var(--glass-text-primary, #242424)',
                  '--tw-ring-color': 'var(--glass-primary, #0099ff)'
                } as React.CSSProperties}
              />
              {citySearch && (
                <button
                  onClick={() => {
                    setCitySearch('');
                    setShowCityDropdown(false);
                    setSelectedCity(null);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--glass-text-disabled)' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--glass-text-secondary, #898989)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--glass-text-disabled)'}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Dropdown risultati città */}
            {showCityDropdown && displayCities.length > 0 && (
              <div 
                className="absolute z-20 w-full mt-1 rounded-md shadow-lg max-h-48 overflow-y-auto"
                style={{
                  background: 'var(--glass-card-bg, #fff)',
                  border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))'
                }}
              >
                {(Array.isArray(displayCities) ? displayCities : []).map((city: any) => (
                  <button
                    key={city.id || city.name}
                    onClick={() => handleCitySelect(city)}
                    className="w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors"
                    style={{
                      color: 'var(--glass-text-primary, #242424)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--glass-hover-bg, #f3f4f6)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span className="font-medium" style={{ color: 'var(--glass-text-primary, #242424)' }}>{city.name}</span>
                    <span style={{ color: 'var(--glass-text-secondary, #898989)' }} className="text-xs">{city.province}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selezione Provincia */}
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Seleziona provincia</label>
            <select
              value={selectedProvince}
              onChange={(e) => handleProvinceChange(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--glass-card-bg, #fff)',
                border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                color: 'var(--glass-text-primary, #242424)',
                '--tw-ring-color': 'var(--glass-primary, #0099ff)'
              } as React.CSSProperties}
            >
              <option value="">Tutte le province</option>
              {(Array.isArray(provinces) ? provinces : []).map((p: string) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Selezione Regione */}
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--glass-text-secondary, #898989)' }}>Seleziona regione</label>
            <select
              value={selectedRegion}
              onChange={(e) => handleRegionChange(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--glass-card-bg, #fff)',
                border: '1px solid var(--glass-card-border, rgba(34, 42, 53, 0.08))',
                color: 'var(--glass-text-primary, #242424)',
                '--tw-ring-color': 'var(--glass-primary, #0099ff)'
              } as React.CSSProperties}
            >
              <option value="">Tutte le regioni</option>
              {(Array.isArray(regions) ? regions : []).map((r: string) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Città dalla provincia/regione selezionata */}
          {(selectedProvince || selectedRegion) && displayCities.length > 0 && (
            <div className="max-h-32 overflow-y-auto rounded-lg">
              {(Array.isArray(displayCities) ? displayCities.slice(0, 10) : []).map((city: any) => (
                <button
                  key={city.id || city.name}
                  onClick={() => handleCitySelect(city)}
                  className="w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors"
                  style={{
                    backgroundColor: selectedCity?.name === city.name 
                      ? 'var(--glass-primary-light)' 
                      : 'transparent',
                    color: 'var(--glass-text-primary, #242424)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = selectedCity?.name === city.name 
                      ? 'var(--glass-primary-light)' 
                      : 'var(--glass-hover-bg, #f3f4f6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = selectedCity?.name === city.name 
                      ? 'var(--glass-primary-light)' 
                      : 'transparent';
                  }}
                >
                  <span>{city.name}</span>
                  <span className="text-xs" style={{ color: 'var(--glass-text-secondary, #898989)' }}>{city.province}</span>
                </button>
              ))}
              {displayCities.length > 10 && (
                <div className="px-3 py-1 text-xs text-center" style={{ color: 'var(--glass-text-secondary, #898989)' }}>
                  +{displayCities.length - 10} altre città
                </div>
              )}
            </div>
          )}

          {/* Badge distanza selezionata */}
          {hasActiveFilters && (
            <div 
              className="border rounded-lg p-2 flex items-center justify-between"
              style={{
                backgroundColor: 'var(--glass-primary-light)',
                borderColor: 'var(--glass-primary, rgba(0, 153, 255, 0.3))',
                color: 'var(--glass-primary, #0099ff)'
              }}
            >
              <span className="text-sm">Riferimento:</span>
              <span className="text-sm font-medium">{getDistanceDisplay()}</span>
            </div>
          )}

          {/* Selezione raggio di ricerca */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--glass-text-primary, #242424)' }}>Raggio di ricerca</label>
            <div className="grid grid-cols-3 gap-2">
              {distanceRanges.map((range) => (
                <button
                  key={range.max}
                  onClick={() => onRadiusChange?.(range.max)}
                  className="py-2 px-2 rounded-lg text-xs font-medium transition-all"
                  style={{
                    backgroundColor: currentRadius === range.max && hasActiveFilters
                      ? 'var(--glass-primary-light)'
                      : 'var(--glass-card-bg, #fff)',
                    color: currentRadius === range.max && hasActiveFilters
                      ? 'var(--glass-primary, #0099ff)'
                      : 'var(--glass-text-primary, #242424)',
                    border: '1px solid ' + (currentRadius === range.max && hasActiveFilters
                      ? 'var(--glass-primary, rgba(0, 153, 255, 0.3))'
                      : 'var(--glass-card-border, rgba(34, 42, 53, 0.08))')
                  }}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reset filtri */}
          {hasActiveFilters && (
            <button
              onClick={handleReset}
              className="w-full py-2 text-sm text-red-600 hover:text-red-800 border border-red-200 rounded-lg hover:bg-red-50"
            >
              Resetta filtro geografico
            </button>
          )}
        </div>
      )}
    </div>
  );
}
